/**
 * ToastHost — global toast notifications mount point.
 *
 * Subscribes to /notifications/stream (SSE) and shows a sonner toast for each
 * new unread notification. Tapping the toast navigates to /inbox.
 *
 * Auth required (athlete token via cookie). When unauthenticated, EventSource
 * silently fails and re-tries — that's fine.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Toaster, toast } from "sonner";
import { useMe } from "../auth";
import { useTheme } from "../useTheme";
import { API_BASE } from "../api";

interface NotifEvent {
  id: number;
  template_key: string | null;
  category: string | null;
  severity: string | null;
  content: string | null;
  deep_link: string | null;
  created_at: string;
}

const SEEN_KEY = "inbox_toast_seen_ids";
const SEEN_CHANNEL = "inbox_toast_seen";
const MAX_REMEMBER = 100;
// Backoff bounds for SSE reconnect — real exponential, not the old fixed 5s.
const RECONNECT_MIN_MS = 5_000;
const RECONNECT_MAX_MS = 60_000;

// D-4: dedup lives in localStorage (shared across tabs), NOT sessionStorage
// (per-tab). Before, each open tab kept its own seen-set → the same
// notification toasted once per tab, reading as "a message keeps popping up".
function loadSeen(): Set<number> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveSeen(s: Set<number>) {
  const arr = Array.from(s).slice(-MAX_REMEMBER);
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    // silent: localStorage unavailable (private mode / quota)
  }
}

/**
 * Заголовок тоста по категории.
 *
 * ЧЕРЕЗ СЛОВАРЬ, А НЕ ЛИТЕРАЛАМИ (2026-08-04). Здесь лежали русские строки
 * прямо в коде, и атлет с интерфейсом на английском, немецком или украинском
 * получал «Утро» и «Тренер» — единственное место приложения, которое не
 * переводилось. Guard'ы это пропускали: они ищут русский текст в JSX, а тут
 * объект. Незнакомая категория возвращается как есть — тост не должен пропасть
 * из-за того, что бэкенд завёл новую.
 */
function categoryLabel(cat: string | null, t: TFunction): string {
  if (!cat) return t("toast.category.default", { defaultValue: "Уведомление" });
  return t(`toast.category.${cat}`, { defaultValue: cat });
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

export function ToastHost() {
  const nav = useNavigate();
  const { t } = useTranslation("nav");
  const { isDark } = useTheme();
  const { data: me } = useMe();
  const esRef = useRef<EventSource | null>(null);
  const seenRef = useRef<Set<number>>(loadSeen());
  const isAuthed = me != null;

  useEffect(() => {
    // A3 sess post-launch: don't open EventSource on pre-auth screens.
    // The /notifications/stream endpoint returns text/html (SPA fallback)
    // for unauth visitors → MIME error spam in the console + Lighthouse
    // best-practices regression. Wait for cookie session before connecting.
    if (!isAuthed) return;

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = RECONNECT_MIN_MS;

    // D-4: cross-tab suppression. localStorage dedup survives reloads/new tabs,
    // but does NOT notify already-open tabs in real time — two tabs receive the
    // same SSE event a moment apart and both toast. BroadcastChannel closes that
    // window: whoever toasts first announces the id, the others suppress it.
    let bc: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== "undefined") {
        bc = new BroadcastChannel(SEEN_CHANNEL);
        bc.onmessage = (ev) => {
          const id = ev.data?.id;
          if (typeof id === "number") seenRef.current.add(id);
        };
      }
    } catch {
      // silent: BroadcastChannel unavailable — localStorage dedup still applies
    }

    function markSeen(id: number) {
      seenRef.current.add(id);
      saveSeen(seenRef.current);
      try {
        bc?.postMessage({ id });
      } catch {
        // silent: channel closed
      }
    }

    function connect() {
      if (cancelled) return;
      try {
        const es = new EventSource(`${API_BASE}/notifications/stream`, { withCredentials: true });
        esRef.current = es;

        es.addEventListener("open", () => {
          reconnectDelay = RECONNECT_MIN_MS;  // healthy connection → reset backoff
        });

        es.addEventListener("message", (ev) => {
          try {
            const data = JSON.parse(ev.data) as NotifEvent;
            if (!data || typeof data.id !== "number") return;
            if (seenRef.current.has(data.id)) return;
            markSeen(data.id);

            const cat = categoryLabel(data.category, t);
            const preview = data.content
              ? stripHtml(data.content).slice(0, 120)
              : data.template_key
                || t("toast.new", { defaultValue: "Новое уведомление" });

            toast(cat, {
              description: preview,
              action: {
                label: t("toast.open", { defaultValue: "Открыть" }),
                onClick: () => nav("/inbox"),
              },
              duration: 6000,
            });
          } catch (e) {
            // silent: malformed JSON in stored/streamed payload
          }
        });

        es.addEventListener("error", () => {
          es.close();
          esRef.current = null;
          scheduleReconnect();
        });
      } catch {
        // EventSource construction failed — back off and retry
        scheduleReconnect();
      }
    }

    // Real exponential backoff with a hard cap: 5s → 10s → 20s → 40s → 60s.
    // Reset to 5s on a healthy "open". Prevents the old fixed-5s retry storm
    // when the stream flaps or the endpoint is unreachable.
    function scheduleReconnect() {
      if (cancelled || reconnectTimer) return;
      const delay = reconnectDelay;
      reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    }

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      if (bc) {
        bc.close();
        bc = null;
      }
    };
  }, [nav, isAuthed, t]);

  return (
    <Toaster
      position="top-center"
      /* Тема следует за приложением, а не прибита к тёмной: на светлой теме
         тост оставался единственным чёрным окном на белом экране. */
      theme={isDark ? "dark" : "light"}
      richColors
      closeButton
      toastOptions={{
        /* Токены, а не литералы rgb(28 28 30): те не знали про светлую тему и
           не поехали бы за правкой бренда — ровно случай индиго #6E7BFF,
           переставшего существовать, но пережившего миграцию в литералах. */
        style: {
          background: "var(--bg-raised)",
          color: "var(--fg-primary)",
          border: "1px solid var(--bg-divider)",
        },
      }}
    />
  );
}
